/**
 * LoginPortalPage — entrada Gateway solo para Tiger (mobile WebView).
 * Web (browser): no usa este portal; cada cliente usa su login en su DNS.
 * Data flow Tiger: email/password → POST login (client_channel=tiger) → mobile-routing callback.
 * Branding: cliente_config.branding vía GET /v1/public-branding o payload 409.
 * Biometria: boton huella siempre visible en Tiger; Auth/Portal validan al intentar.
 * No se oculta por opt-in Header ni por feature flag UI — ver docs/BIOMETRIC_LOGIN_ENTRY.md.
 * iOS: iosNoZoom + focus/blur viewport fix evita auto-zoom Safari (mismo patron que DateInput/InputField).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import LoginTransitionLoader, {
    clearLoginTransitionProgress,
    isLoginTransitionActive,
    startLoginTransitionProgress,
} from './components/LoginTransitionLoader';
import { clearAuthTransitionWithFade, markAuthTransitionMilestone } from './utils/authTransition';
import {
    portalLogin,
    clearResidualSessionsOnLoginScreen,
    fetchPortalPublicBranding,
    fetchPortalPublicFeatures,
    portalBiometricRouteLogin,
} from './services/portalAuthApi';
import {
    supportsWebAuthn,
    mapWebAuthnError,
} from './utils/webauthn';
import {
    cancelActiveWebAuthnCeremony,
} from './utils/webauthn-runtime';
import {
    isLoginBiometricEnabled,
    LOGIN_BIOMETRIC_ENABLED_KEY,
    setBiometricLoginPreference,
    consumeBiometricOptInCookie,
} from './utils/biometricLoginPreferenceStorage';
import { getLastLoginEmail, persistLastLoginEmail } from './utils/lastLoginEmailStorage';
import {
    LOGIN_INVALID_CREDENTIALS,
    LOGIN_ACCOUNT_LOCKED,
    LOGIN_BIOMETRIC_FAILED,
    LOGIN_RATE_LIMITED,
    LOGIN_AMBIGUOUS_CLIENT,
    LOGIN_FIELDS_REQUIRED,
    mapPortalLoginError,
} from './utils/loginSecurityMessages';
import { isTigerEnvironment, isTigerSimActive } from './tigerDetector';
import { simulateTigerExchangeAndGetTenantUrl } from './services/tigerExchangeSim';
import {
    trySameOriginOpaqueHandoff,
    warmupTenantNavigation,
} from './services/portalTenantWarmup';
import {
    attemptTigerSessionResume,
    clearResumeRedirectMarks,
    clearTigerPortalResumeArtifacts,
    parseTigerResumeQuery,
    persistTigerTenantFrontOrigin,
    PORTAL_SKIP_RESUME_QUERY,
    prepareLoginPortalEntry,
    RESUME_DEADLINE_MS,
    shouldAttemptTigerSessionResume,
} from './services/tigerSessionResume';
import {
    BRIDGE_CODES,
    SECRET_TYPE_PASSWORD,
    isTigerBiometricCredentialBridgeAvailable,
    saveTigerBiometricCredential,
    unlockTigerBiometricCredential,
} from './tiger/tigerBiometricCredential';
import {
    applyPortalBrandTheme,
    getDefaultPortalLogo,
    normalizePortalBranding,
    resolvePortalLogoSrc,
} from './utils/applyPortalBrandTheme';
import { getPersistedTigerTenantKey } from './services/persistPortalHandoffSession';
import { useTheme } from './contexts/ThemeContext';
import { syncTigerIosChromeColors } from './tiger/tigerBridgeChromeColor';
import './styles/LoginPortal.css';

/** Rem del font-size de .auth-input (0.75rem). Mismo factor que DateInput / InputField / UserCard. */
const IOS_SCALE_FACTOR = 0.75;
const FOCUS_SCROLL_DELAY_MS = 100;
const BLUR_SCROLL_DELAY_MS = 50;

/** Badge de canal (build-time). Gateway compartido demo: "LoginPortal DEMO · Gateway Tiger". */
const PORTAL_CHANNEL_BADGE =
    String(process.env.REACT_APP_PORTAL_CHANNEL_BADGE || '').trim()
    || 'LoginPortal · Gateway Tiger';

/** Deteccion iOS (Safari / WKWebView Tiger / iPadOS). Copia local: LoginPortal no importa @wms/shared. */
function isIOSDevice() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/i.test(ua)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function resetDocumentGhostScroll() {
    if (typeof window === 'undefined') return;
    window.scrollTo(0, 0);
    const html = document.documentElement;
    html.scrollLeft = 0;
    html.scrollTop = 0;
    if (document.body) {
        document.body.scrollLeft = 0;
        document.body.scrollTop = 0;
    }
}

/**
 * Parche iOS anti auto-zoom + correccion de scroll al focus.
 * Estilos inline equivalentes a mixin .iosNoZoom (sin tocar LoginPortal.css).
 */
function useIosInputNoZoom(remScaleFactor) {
    const [isIOS, setIsIOS] = useState(() => {
        if (typeof window === 'undefined') return false;
        return isIOSDevice();
    });
    const focusTimerRef = useRef(0);
    const blurTimerRef = useRef(0);

    useEffect(() => {
        setIsIOS(isIOSDevice());
        return () => {
            if (focusTimerRef.current) window.clearTimeout(focusTimerRef.current);
            if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
        };
    }, []);

    if (!isIOS || !(Number(remScaleFactor) > 0)) {
        return {
            className: '',
            style: undefined,
            onFocus: undefined,
            onBlur: undefined,
        };
    }

    const onFocus = (event) => {
        const el = event.currentTarget;
        if (focusTimerRef.current) window.clearTimeout(focusTimerRef.current);
        focusTimerRef.current = window.setTimeout(() => {
            resetDocumentGhostScroll();
            try {
                el?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            } catch {
                el?.scrollIntoView?.(true);
            }
        }, FOCUS_SCROLL_DELAY_MS);
    };

    const onBlur = () => {
        if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
        blurTimerRef.current = window.setTimeout(() => {
            resetDocumentGhostScroll();
        }, BLUR_SCROLL_DELAY_MS);
    };

    return {
        // Marker no vacio: iosNoZoomFieldProps activa el merge de focus/blur/style.
        className: 'iosNoZoom',
        style: {
            '--ios-scale-factor': Number(remScaleFactor),
            fontSize: '16px',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            transform: 'none',
            zoom: 'normal',
        },
        onFocus,
        onBlur,
    };
}

function iosNoZoomFieldProps(iosNoZoom, extra = {}) {
    const {
        className: extraClass = '',
        style,
        onFocus,
        onBlur,
        ...rest
    } = extra;

    const className = `${extraClass} ${iosNoZoom?.className || ''}`.trim();

    if (!iosNoZoom?.className) {
        return {
            ...rest,
            className: className || undefined,
            style,
            onFocus,
            onBlur,
        };
    }

    return {
        ...rest,
        className,
        style: { ...(style && typeof style === 'object' ? style : null), ...iosNoZoom.style },
        'data-ios-no-zoom': '',
        onFocus: (event) => {
            iosNoZoom.onFocus?.(event);
            if (typeof onFocus === 'function') onFocus(event);
        },
        onBlur: (event) => {
            iosNoZoom.onBlur?.(event);
            if (typeof onBlur === 'function') onBlur(event);
        },
    };
}

const LoginPortalPage = () => {
    const [email, setEmail] = useState(() => getLastLoginEmail());
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [forgotOpen, setForgotOpen] = useState(false);
    const [clientes, setClientes] = useState([]);
    const [clienteCodigo, setClienteCodigo] = useState('');
    const [branding, setBranding] = useState(() => normalizePortalBranding(null));
    const [biometricFeatureEnabled, setBiometricFeatureEnabled] = useState(false);
    const [nativeCredentialBridge, setNativeCredentialBridge] = useState(false);
    const [webauthnLoading, setWebauthnLoading] = useState(false);
    /** Post-logout: continuidad barra hasta que el form esté listo. */
    const [postLogoutOverlay, setPostLogoutOverlay] = useState(() => {
        if (typeof window === 'undefined') return false;
        const query = parseTigerResumeQuery(window.location.search);
        return query.loggedOut && isLoginTransitionActive();
    });
    const [postLogoutFadeOut, setPostLogoutFadeOut] = useState(false);
    /** Spinner en botón password; fullscreen loader solo tras login OK (redirect). */
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [loginBiometricEnabled, setLoginBiometricEnabled] = useState(() => {
        // Cookie de begin-logout (preprod cross-origin) antes de leer localStorage.
        consumeBiometricOptInCookie();
        return isLoginBiometricEnabled();
    });
    /** 'password' | 'biometric' — al elegir biometría se oculta la contraseña. */
    const [authMethod, setAuthMethod] = useState('password');
    const isTiger = isTigerEnvironment();
    const isBiometricMethod = authMethod === 'biometric';
    const tigerSim = isTigerSimActive();
    // Huella siempre visible en Tiger; Auth/Portal validan al intentar (registro, flag server, device).
    // Opt-in Header no oculta el botón. Ver LoginPortal/docs/BIOMETRIC_LOGIN_ENTRY.md.
    const showBiometricEntry = isTiger;

    const logoSrc = useMemo(() => resolvePortalLogoSrc(branding), [branding]);
    const displayName = branding.name || 'WMS';
    const iosNoZoom = useIosInputNoZoom(IOS_SCALE_FACTOR);
    const { isDarkMode, toggleTheme } = useTheme();

    // Entrada portal: no clear de transición en mount (logout mantiene barra continua).
    useLayoutEffect(() => {
        setLoading(false);
        prepareLoginPortalEntry(window.location.search);
    }, []);

    // Post-logout / bounce: fade-out y clear único cuando el form va a mostrarse.
    useLayoutEffect(() => {
        if (!postLogoutOverlay) return undefined;
        const query = parseTigerResumeQuery(window.location.search);
        if (!query.loggedOut && !query.skipResume) return undefined;

        let cancelled = false;
        setPostLogoutFadeOut(true);
        void clearAuthTransitionWithFade().then(() => {
            if (!cancelled) {
                setPostLogoutOverlay(false);
                setPostLogoutFadeOut(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [postLogoutOverlay]);

    useEffect(() => {
        const onPageShow = (event) => {
            if (!event.persisted) return;
            setLoading(false);
            if (!isLoginTransitionActive()) {
                clearLoginTransitionProgress();
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    useEffect(() => {
        applyPortalBrandTheme(branding);
        syncTigerIosChromeColors({ preset: 'portal' });
    }, [branding, isDarkMode]);

    useEffect(() => () => cancelActiveWebAuthnCeremony(), []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const data = await fetchPortalPublicFeatures();
            if (cancelled) return;
            setBiometricFeatureEnabled(Boolean(data?.features?.biometric_webauthn));
            setNativeCredentialBridge(
                isTigerEnvironment() && isTigerBiometricCredentialBridgeAvailable()
            );
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handlePortalAuthError = useCallback((err) => {
        if (err.status === 409 && err.payload?.code === 'PORTAL_CLIENT_AMBIGUOUS') {
            const list = err.payload?.data?.clientes || [];
            setClientes(list);
            setClienteCodigo(list[0]?.codigo || '');
            if (list[0]?.branding) {
                setBranding(normalizePortalBranding(list[0].branding));
            }
            setError(LOGIN_AMBIGUOUS_CLIENT);
            return;
        }
        if (
            err.status === 429 ||
            err.payload?.code === 'AUTH_RATE_LIMITED' ||
            err.payload?.code === 'RATE_LIMITED'
        ) {
            setClientes([]);
            setClienteCodigo('');
            setError(LOGIN_RATE_LIMITED);
            return;
        }
        if (err.status === 423 || err.payload?.code === 'ACCOUNT_LOCKED') {
            setClientes([]);
            setClienteCodigo('');
            setError(LOGIN_ACCOUNT_LOCKED);
            return;
        }
        setClientes([]);
        setClienteCodigo('');
        setError(mapPortalLoginError(err, LOGIN_INVALID_CREDENTIALS));
    }, []);

    const completePortalRedirect = useCallback(async (data) => {
        markAuthTransitionMilestone('PORTAL_OK');
        if (data?.config?.branding) {
            applyPortalBrandTheme(normalizePortalBranding(data.config.branding));
            syncTigerIosChromeColors({ preset: 'portal' });
        }
        if (data?.front_base_url) {
            persistTigerTenantFrontOrigin(data.front_base_url);
        }
        if (tigerSim && data.channel === 'tiger' && data.redirect_to) {
            const tenantUrl = await simulateTigerExchangeAndGetTenantUrl(data.redirect_to);
            try {
                persistTigerTenantFrontOrigin(new URL(tenantUrl, window.location.origin).origin);
            } catch {
                /* ignore */
            }
            warmupTenantNavigation(tenantUrl);
            // Mismo host (lab): handoff acá y entrar a la app sin montar /m/:id.
            const sameOrigin = await trySameOriginOpaqueHandoff(tenantUrl, { tigerSim: true });
            if (sameOrigin.ok) return;
            const dest = new URL(tenantUrl, window.location.origin);
            dest.searchParams.set('tigerSim', '1');
            window.location.replace(dest.toString());
            return;
        }
        if (data?.redirect_to) {
            try {
                const maybeTenant = new URL(data.redirect_to, window.location.origin);
                if (!data?.front_base_url && /\/m\//i.test(maybeTenant.pathname)) {
                    persistTigerTenantFrontOrigin(maybeTenant.origin);
                }
                // Si el redirect ya apunta a /m/, calentar igual.
                if (/\/m\//i.test(maybeTenant.pathname)) {
                    warmupTenantNavigation(maybeTenant.toString());
                }
            } catch {
                /* ignore */
            }
        }
        window.location.replace(data.redirect_to);
    }, [tigerSim]);

    const resumeAbortRef = useRef(/** @type {AbortController|null} */ (null));
    const [resumeResolving, setResumeResolving] = useState(() => {
        if (typeof window === 'undefined') return false;
        try {
            if (!isTigerEnvironment()) return false;
            const { runResume } = prepareLoginPortalEntry(window.location.search);
            if (!runResume) return false;
            return shouldAttemptTigerSessionResume({
                isTiger: true,
                loggedOut: false,
                skipResume: false,
                localStorage: window.localStorage,
            });
        } catch {
            return false;
        }
    });

    const cancelPortalResume = useCallback((reason = 'user_intent') => {
        const ctrl = resumeAbortRef.current;
        if (ctrl && !ctrl.signal.aborted) {
            try {
                ctrl.abort(reason);
            } catch {
                ctrl.abort();
            }
        }
        resumeAbortRef.current = null;
        setResumeResolving(false);
    }, []);

    // Resume sesión Tiger: loader (no form) mientras decide; abortable por intención/deadline.
    useEffect(() => {
        if (!isTiger) return undefined;
        const { runResume } = prepareLoginPortalEntry(window.location.search);
        if (!runResume) {
            setResumeResolving(false);
            return undefined;
        }
        const shouldRun = shouldAttemptTigerSessionResume({
            isTiger: true,
            loggedOut: false,
            skipResume: false,
            localStorage: window.localStorage,
        });
        if (!shouldRun) {
            setResumeResolving(false);
            return undefined;
        }

        const ctrl = new AbortController();
        resumeAbortRef.current = ctrl;
        setResumeResolving(true);
        const deadlineId = window.setTimeout(() => {
            if (!ctrl.signal.aborted) {
                try {
                    ctrl.abort('deadline');
                } catch {
                    ctrl.abort();
                }
            }
        }, RESUME_DEADLINE_MS);

        void (async () => {
            try {
                const result = await attemptTigerSessionResume({
                    tigerSim,
                    signal: ctrl.signal,
                });
                if (ctrl.signal.aborted) return;
                if (!result?.resumed) {
                    setResumeResolving(false);
                }
                // resumed=true: location.replace en curso; mantener loader.
            } catch {
                if (!ctrl.signal.aborted) setResumeResolving(false);
            }
        })();

        return () => {
            window.clearTimeout(deadlineId);
            if (!ctrl.signal.aborted) {
                try {
                    ctrl.abort('unmount');
                } catch {
                    ctrl.abort();
                }
            }
            if (resumeAbortRef.current === ctrl) {
                resumeAbortRef.current = null;
            }
        };
    }, [isTiger, tigerSim]);

    // En pantalla de login Gateway no debe quedar cookies portal ni artefactos de resume/loader.
    // Opt-in biometrico: query desde tenant (?wms_login_biometric_enabled=1) o cookie de begin-logout.
    useEffect(() => {
        let cancelled = false;
        if (!isTiger) return undefined;
        (async () => {
            try {
                const params = new URLSearchParams(window.location.search || '');
                const biometricFromCookie = consumeBiometricOptInCookie();
                const biometricFromTenant = params.get(LOGIN_BIOMETRIC_ENABLED_KEY) === '1';
                if (biometricFromTenant || biometricFromCookie) {
                    setBiometricLoginPreference(true);
                    setLoginBiometricEnabled(true);
                    if (biometricFromTenant) {
                        params.delete(LOGIN_BIOMETRIC_ENABLED_KEY);
                    }
                }
                const loggedOut = params.get('logged_out') === '1';
                const skipResume = params.get(PORTAL_SKIP_RESUME_QUERY) === '1';
                if (loggedOut) {
                    clearTigerPortalResumeArtifacts();
                    if (isLoginTransitionActive()) {
                        setPostLogoutOverlay(true);
                    }
                    params.delete('logged_out');
                }
                if (skipResume) {
                    // No auto-resume en esta carga; conservar lifeline (refresh / tenant origin).
                    clearResumeRedirectMarks();
                    if (isLoginTransitionActive()) {
                        setPostLogoutOverlay(true);
                    } else {
                        clearLoginTransitionProgress();
                    }
                    params.delete(PORTAL_SKIP_RESUME_QUERY);
                }
                if (biometricFromTenant || biometricFromCookie || loggedOut || skipResume) {
                    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash || ''}`;
                    window.history.replaceState({}, '', next);
                }
                if (loggedOut) return;
                await clearResidualSessionsOnLoginScreen();
                if (cancelled) return;
            } catch {
                /* best-effort */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isTiger]);

    // Al elegir cliente (picker), codigo en query, o último tenant del handoff (post-logout).
    useEffect(() => {
        let cancelled = false;
        const fromQuery = (() => {
            try {
                return new URLSearchParams(window.location.search || '').get('cliente_codigo') || '';
            } catch {
                return '';
            }
        })();
        const fromStorage = getPersistedTigerTenantKey();
        const codigo = String(clienteCodigo || fromQuery || fromStorage || '').trim();
        if (!codigo) {
            setBranding(normalizePortalBranding(null));
            return undefined;
        }

        const fromList = clientes.find((c) => String(c.codigo) === codigo);
        if (fromList?.branding) {
            setBranding(normalizePortalBranding(fromList.branding));
            return undefined;
        }

        (async () => {
            const data = await fetchPortalPublicBranding(codigo);
            if (cancelled || !data?.branding) return;
            setBranding(normalizePortalBranding(data.branding));
        })();

        return () => {
            cancelled = true;
        };
    }, [clienteCodigo, clientes]);

    const handleHeaderLogoError = useCallback((event) => {
        const img = event?.currentTarget;
        const fallback = getDefaultPortalLogo();
        if (!img || !fallback || img.dataset.logoFallback === '1') return;
        img.dataset.logoFallback = '1';
        img.src = fallback;
    }, []);

    const persistNativeCredentialAfterPasswordLogin = useCallback(async (userEmail, userPassword) => {
        // Solo Tiger mobile (o tigerSim). Web tenant no persiste secretos en bridge.
        if (!isTigerEnvironment()) return;
        // Best-effort: no bloquea el redirect ni expone el secreto en UI/logs.
        try {
            await saveTigerBiometricCredential(userEmail, SECRET_TYPE_PASSWORD, userPassword);
        } catch {
            /* ignore */
        }
    }, []);

    const switchToPasswordMethod = () => {
        setAuthMethod('password');
        setError('');
        setWebauthnLoading(false);
        clearLoginTransitionProgress();
    };

    const switchToBiometricMethod = () => {
        setAuthMethod('biometric');
        setPassword('');
        setShowPassword(false);
        setError('');
        setForgotOpen(false);
    };

    useEffect(() => {
        if (!showBiometricEntry && authMethod === 'biometric') {
            setAuthMethod('password');
            setWebauthnLoading(false);
            clearLoginTransitionProgress();
        }
    }, [authMethod, showBiometricEntry]);

    const handleNativeCredentialLogin = useCallback(async (normalizedEmail) => {
        setWebauthnLoading(true);
        let secretForLogin = '';
        try {
            const unlocked = await unlockTigerBiometricCredential(normalizedEmail);
            if (!unlocked.ok) {
                if (unlocked.code !== BRIDGE_CODES.CANCELLED && unlocked.message) {
                    setError(LOGIN_BIOMETRIC_FAILED);
                }
                setWebauthnLoading(false);
                return;
            }
            if (unlocked.secretType !== SECRET_TYPE_PASSWORD) {
                setError(LOGIN_BIOMETRIC_FAILED);
                setWebauthnLoading(false);
                return;
            }

            secretForLogin = unlocked.secret;
            setWebauthnLoading(false);

            const data = await portalLogin(
                normalizedEmail,
                secretForLogin,
                clienteCodigo || undefined,
            );
            persistLastLoginEmail(normalizedEmail);
            startLoginTransitionProgress();
            setLoading(true);
            await completePortalRedirect(data);
        } catch (err) {
            handlePortalAuthError(err);
            clearLoginTransitionProgress();
            setWebauthnLoading(false);
            setLoading(false);
        } finally {
            secretForLogin = '';
        }
    }, [
        clienteCodigo,
        completePortalRedirect,
        handlePortalAuthError,
    ]);

    const handleBiometricLogin = async () => {
        if (webauthnLoading || loading || passwordSubmitting) return;
        setError('');

        if (!isTiger) return;

        // Solo cambia de modo; el prompt se dispara con "Iniciar con biometría".
        if (!isBiometricMethod) {
            switchToBiometricMethod();
            return;
        }

        // Intención del usuario > resume automático (no entrar sin biometría).
        cancelPortalResume('biometric_intent');

        const normalizedEmail = String(email || '').trim();
        if (!normalizedEmail) {
            setError('Ingresá tu email para usar biometría.');
            return;
        }

        // Option B (Keystore): solo si el WebView no expone WebAuthn pero sí el bridge nativo.
        if (!supportsWebAuthn() && isTigerBiometricCredentialBridgeAvailable()) {
            await handleNativeCredentialLogin(normalizedEmail);
            return;
        }

        // Camino Auth: siempre route-login → ceremonia en DNS tenant.
        // No cortar en cliente por supportsWebAuthn; Portal/Auth validan registro/flag.
        setWebauthnLoading(true);
        try {
            const routeData = await portalBiometricRouteLogin(
                normalizedEmail,
                clienteCodigo || undefined,
            );
            let dest;
            try {
                dest = new URL(routeData.tenant_login_url);
            } catch {
                setError('URL de login del cliente inválida.');
                setWebauthnLoading(false);
                return;
            }
            if (tigerSim) dest.searchParams.set('tigerSim', '1');
            persistLastLoginEmail(normalizedEmail);
            startLoginTransitionProgress();
            markAuthTransitionMilestone('PORTAL_OK');
            window.location.replace(dest.toString());
            return;
        } catch (err) {
            if (err.status === 409 && err.payload?.code === 'PORTAL_CLIENT_AMBIGUOUS') {
                handlePortalAuthError(err);
            } else {
                // Cancelación OS: sin banner rojo (paridad tenant / Option B CANCELLED).
                const biometricMsg = mapWebAuthnError(err, LOGIN_BIOMETRIC_FAILED);
                if (biometricMsg) setError(biometricMsg);
            }
            clearLoginTransitionProgress();
            setWebauthnLoading(false);
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        if (!isTiger) return;

        if (isBiometricMethod) {
            await handleBiometricLogin();
            return;
        }

        if (!email || !password) {
            setError(LOGIN_FIELDS_REQUIRED);
            return;
        }

        cancelPortalResume('password_intent');
        setPasswordSubmitting(true);
        const emailSnapshot = String(email).trim();
        const passwordSnapshot = String(password);
        try {
            const data = await portalLogin(emailSnapshot, passwordSnapshot, clienteCodigo || undefined);
            persistLastLoginEmail(emailSnapshot);
            // Option B: si ya habilitaron biometria desde Header, refrescar secreto nativo.
            if (
                isTiger
                && loginBiometricEnabled
                && nativeCredentialBridge
                && !(supportsWebAuthn() && biometricFeatureEnabled)
            ) {
                void persistNativeCredentialAfterPasswordLogin(emailSnapshot, passwordSnapshot);
            }
            startLoginTransitionProgress();
            setLoading(true);
            await completePortalRedirect(data);
        } catch (err) {
            handlePortalAuthError(err);
            clearLoginTransitionProgress();
            setPasswordSubmitting(false);
        }
    };

    // Loader: resume en curso (cold start), login OK, o continuidad post-logout.
    if (loading || postLogoutOverlay || resumeResolving) {
        return (
            <LoginTransitionLoader
                ariaLabel={
                    postLogoutOverlay
                        ? 'Volviendo al login'
                        : resumeResolving
                            ? 'Restaurando sesión'
                            : 'Ingresando al WMS'
                }
                logoSrc={logoSrc || getDefaultPortalLogo()}
                logoAlt={displayName}
                fadeOut={postLogoutFadeOut}
            />
        );
    }

    return (
        <div className="auth-page">
            <header className="app-header">
                <div className="header-top">
                    <div className="d-flex align-items-center">
                        <img
                            src={logoSrc}
                            className="header-logo"
                            alt={displayName}
                            onError={handleHeaderLogoError}
                        />
                    </div>
                    <button
                        type="button"
                        className="auth-theme-toggle"
                        onClick={toggleTheme}
                        aria-pressed={isDarkMode}
                        aria-label={isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                        title={isDarkMode ? 'Modo claro' : 'Modo oscuro'}
                    >
                        <i className={`bi ${isDarkMode ? 'bi-sun' : 'bi-moon-stars'}`} aria-hidden="true" />
                    </button>
                </div>
            </header>

            <main className="auth-content">
                <div className="auth-card">
                    <div className="auth-card-header">
                        <span className="auth-channel-badge">{PORTAL_CHANNEL_BADGE}</span>
                        <h1 className="auth-title">Bienvenido</h1>
                        <p className="auth-subtitle">
                            {isTiger
                                ? isBiometricMethod
                                    ? 'Ingresá tu email y usá biometría para acceder al WMS'
                                    : tigerSim
                                        ? 'Simulación Tiger (lab): credenciales → exchange → tenant WMS'
                                        : `App móvil: ingresá credenciales para elegir tu cliente WMS${displayName && displayName !== 'WMS' ? ` (${displayName})` : ''}`
                                : 'Solo app Tiger. En la web usá el login WMS de tu empresa.'}
                        </p>
                    </div>

                    {!isTiger ? (
                        <div className="auth-error" role="status">
                            <i className="bi bi-phone" />
                            En la web usá el login de tu empresa (DNS del cliente WMS). El
                            LoginPortal solo opera dentro de la app Tiger.
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="auth-error" role="alert">
                                    <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <form className="auth-form" onSubmit={handleLogin} noValidate>
                                <div className="auth-field">
                                    <label className="auth-label" htmlFor="login-portal-email">
                                        Usuario o Email
                                    </label>
                                    <div className="auth-input-wrapper">
                                        <i className="bi bi-person auth-input-icon" />
                                        <input
                                            {...iosNoZoomFieldProps(iosNoZoom, {
                                                id: 'login-portal-email',
                                                type: 'email',
                                                className: 'auth-input',
                                                placeholder: 'ejemplo@empresa.com',
                                                value: email,
                                                onChange: (e) => {
                                                    setEmail(e.target.value);
                                                    setClientes([]);
                                                    setClienteCodigo('');
                                                },
                                                autoComplete: 'email',
                                                required: true,
                                                disabled: loading || passwordSubmitting,
                                            })}
                                        />
                                    </div>
                                </div>

                                {!isBiometricMethod && (
                                    <div className="auth-field">
                                        <label className="auth-label" htmlFor="login-portal-password">
                                            Contraseña
                                        </label>
                                        <div className="auth-input-wrapper">
                                            <i className="bi bi-lock auth-input-icon" />
                                            <input
                                                {...iosNoZoomFieldProps(iosNoZoom, {
                                                    id: 'login-portal-password',
                                                    type: showPassword ? 'text' : 'password',
                                                    className: 'auth-input has-icon-right',
                                                    placeholder: '***********',
                                                    value: password,
                                                    onChange: (e) => setPassword(e.target.value),
                                                    autoComplete: 'current-password',
                                                    required: !isBiometricMethod,
                                                    disabled: loading || passwordSubmitting,
                                                })}
                                            />
                                            <button
                                                type="button"
                                                className="auth-input-btn"
                                                onClick={() => setShowPassword((p) => !p)}
                                                tabIndex={-1}
                                                aria-label="Mostrar/Ocultar contraseña"
                                            >
                                                <i className={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`} />
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            className="auth-forgot-link"
                                            onClick={() => setForgotOpen(true)}
                                        >
                                            ¿Olvidaste tu contraseña?
                                        </button>
                                    </div>
                                )}

                                {clientes.length > 0 && (
                                    <div className="auth-field">
                                        <label className="auth-label" htmlFor="login-portal-cliente">
                                            Entorno WMS
                                        </label>
                                        <select
                                            {...iosNoZoomFieldProps(iosNoZoom, {
                                                id: 'login-portal-cliente',
                                                className: 'auth-input',
                                                value: clienteCodigo,
                                                onChange: (e) => setClienteCodigo(e.target.value),
                                                disabled: loading || webauthnLoading || passwordSubmitting,
                                                required: true,
                                            })}
                                        >
                                            {clientes.map((c) => (
                                                <option key={c.codigo} value={c.codigo}>
                                                    {c.nombre || c.codigo}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {isBiometricMethod ? (
                                    <>
                                        <button
                                            className="auth-btn-submit auth-btn-submit--biometric"
                                            type="submit"
                                            disabled={loading || webauthnLoading}
                                        >
                                            {webauthnLoading ? (
                                                <>
                                                    <span className="auth-spinner" />
                                                    <span>Verificando biometría...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-fingerprint" aria-hidden="true" />
                                                    <span>Iniciar con biometría</span>
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="auth-method-switch"
                                            onClick={switchToPasswordMethod}
                                            disabled={loading || webauthnLoading}
                                        >
                                            Usar usuario y contraseña
                                        </button>
                                    </>
                                ) : (
                                    <div className="auth-submit-row">
                                        <button
                                            className="auth-btn-submit"
                                            type="submit"
                                            disabled={loading || webauthnLoading || passwordSubmitting}
                                        >
                                            {passwordSubmitting ? (
                                                <>
                                                    <span className="auth-spinner" />
                                                    <span>Ingresando...</span>
                                                </>
                                            ) : (
                                                'Iniciar Sesión'
                                            )}
                                        </button>
                                        {showBiometricEntry && (
                                            <button
                                                type="button"
                                                className="auth-social-btn biometric"
                                                onClick={switchToBiometricMethod}
                                                disabled={webauthnLoading || loading}
                                                aria-label="Cambiar a login con biometría"
                                            >
                                                <i className="bi bi-fingerprint" aria-hidden="true" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </form>
                        </>
                    )}
                </div>
            </main>

            <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
        </div>
    );
};

export default LoginPortalPage;
